import { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Header from './components/Header';
import Hero from './components/Hero';
import ServiceSection from './components/ServiceSection';
import AboutSection from './components/AboutSection';
import ContactSection from './components/ContactSection';
import Footer from './components/Footer';

const AdminModule = lazy(() => import('./admin/AdminModule'));

// Main website component
const MainWebsite = () => {
  const location = useLocation();
  useEffect(() => {
    // Set Estonian language attributes
    document.documentElement.lang = 'et';
    document.documentElement.setAttribute('dir', 'ltr');
    
    // Add Estonian-specific meta tags dynamically
    const metaViewport = document.querySelector('meta[name="viewport"]');
    if (metaViewport) {
      metaViewport.setAttribute('content', 'width=device-width, initial-scale=1.0, user-scalable=yes');
    }

    // Add Estonian timezone
    const now = new Date();
    const estonianTime = new Intl.DateTimeFormat('et-EE', {
      timeZone: 'Europe/Tallinn',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }).format(now);
    
    console.log('Estonian time:', estonianTime);

    // Estonian keyboard support
    document.addEventListener('keydown', (e) => {
      // Support for Estonian characters
      if (e.altKey) {
        switch (e.key) {
          case 'a':
            e.preventDefault();
            break;
          case 'o':
            e.preventDefault();
            break;
          case 'u':
            e.preventDefault();
            break;
        }
      }
    });

    // Apply SEO settings from CMS
    (async () => {
      try {
        const API_BASE = (import.meta as any)?.env?.VITE_API_URL || (typeof window !== 'undefined' && window.location && window.location.port === '5173' ? 'http://localhost:3002' : '');
        const res = await fetch(`${API_BASE}/api/settings`);
        const json = await res.json();
        if (json?.ok && json?.data) {
          const s = json.data;
          if (s.site_title) document.title = s.site_title;
          // description
          let desc = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
          if (!desc) {
            desc = document.createElement('meta');
            desc.setAttribute('name', 'description');
            document.head.appendChild(desc);
          }
          if (s.meta_description) desc.setAttribute('content', s.meta_description);
          // robots
          let robots = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
          if (!robots) {
            robots = document.createElement('meta');
            robots.setAttribute('name', 'robots');
            document.head.appendChild(robots);
          }
          robots.setAttribute('content', s.indexing_allowed === 0 ? 'noindex, nofollow' : 'index, follow');
          // canonical
          let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
          if (!canonical) {
            canonical = document.createElement('link');
            canonical.setAttribute('rel', 'canonical');
            document.head.appendChild(canonical);
          }
          if (s.canonical_url) canonical.setAttribute('href', s.canonical_url);
          // OpenGraph
          const ensureOg = (property: string, value: string | null | undefined) => {
            if (!value) return;
            let el = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
            if (!el) {
              el = document.createElement('meta');
              el.setAttribute('property', property);
              document.head.appendChild(el);
            }
            el.setAttribute('content', value);
          };
          ensureOg('og:title', s.site_title);
          ensureOg('og:description', s.meta_description);
          ensureOg('og:image', s.og_image_url);
          ensureOg('og:url', s.canonical_url);
          ensureOg('og:type', 'website');
          // Ensure locale is Estonian
          let ogLocale = document.querySelector('meta[property="og:locale"]') as HTMLMetaElement | null;
          if (!ogLocale) {
            ogLocale = document.createElement('meta');
            ogLocale.setAttribute('property', 'og:locale');
            document.head.appendChild(ogLocale);
          }
          ogLocale.setAttribute('content', 'et_EE');
          // Twitter
          let tw = document.querySelector('meta[name="twitter:card"]') as HTMLMetaElement | null;
          if (!tw) {
            tw = document.createElement('meta');
            tw.setAttribute('name', 'twitter:card');
            document.head.appendChild(tw);
          }
          tw.setAttribute('content', 'summary_large_image');
          if (s.twitter_handle) {
            let ts = document.querySelector('meta[name="twitter:site"]') as HTMLMetaElement | null;
            if (!ts) {
              ts = document.createElement('meta');
              ts.setAttribute('name', 'twitter:site');
              document.head.appendChild(ts);
            }
            ts.setAttribute('content', s.twitter_handle);
          }
        }
      } catch {}
    })();

    return () => {
      document.removeEventListener('keydown', () => {});
    };
  }, []);

  // Scroll to section based on Estonian slug path
  useEffect(() => {
    const path = location.pathname;
    const pathToId: Record<string, string> = {
      '/': 'hero',
      '/avaleht': 'hero',
      '/teenused': 'services',
      '/meist': 'about',
      '/kontakt': 'contact',
    };
    const targetId = pathToId[path];
    if (targetId) {
      const el = document.getElementById(targetId);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, [location.pathname]);

  // Keep canonical and og:url in sync with current path
  useEffect(() => {
    try {
      const canonicalEl = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      let base = window.location.origin;
      if (canonicalEl?.href) {
        const parsed = new URL(canonicalEl.href);
        base = `${parsed.origin}`;
      }
      if (canonicalEl) canonicalEl.setAttribute('href', `${base}${location.pathname}`);
      const ogUrl = document.querySelector('meta[property="og:url"]') as HTMLMetaElement | null;
      if (ogUrl) ogUrl.setAttribute('content', `${base}${location.pathname}`);
    } catch {}
  }, [location.pathname]);

  return (
    <div className="font-body text-gray-800 bg-gray-50 min-h-screen flex flex-col">
      <Header />
      <main className="flex-grow">
        <Hero />
        <ServiceSection />
        <AboutSection />
        <ContactSection />
      </main>
      <Footer />
      
      {/* Back to top button */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="fixed bottom-8 right-8 bg-primary-700 hover:bg-primary-800 text-white p-3 rounded-full shadow-lg transition-all duration-300 z-40"
        aria-label="Tagasi üles"
      >
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 10l7-7m0 0l7 7m-7-7v18" />
        </svg>
      </button>
    </div>
  );
};

function App() {
  return (
    <Router>
      <Routes>
        {/* Main website */}
        <Route path="/" element={<MainWebsite />} />
        <Route path="/avaleht" element={<MainWebsite />} />
        <Route path="/teenused" element={<MainWebsite />} />
        <Route path="/meist" element={<MainWebsite />} />
        <Route path="/kontakt" element={<MainWebsite />} />

        {/* Admin module (lazy-loaded) */}
        <Route
          path="/admin/*"
          element={
            <Suspense
              fallback={
                <div className="min-h-screen flex items-center justify-center text-gray-600">Laadimine…</div>
              }
            >
              <AdminModule />
            </Suspense>
          }
        />

        {/* Catch all route */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

export default App;